import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DateTime } from "luxon";

// Generate a unique client ID for this browser window/tab
const clientID = Math.round(Math.random() * 10 ** 16);
// console.log(`Generated unique clientID for this window: ${clientID}`);

const initialState: ReplayConfig = {
  isReplay: false,
  // startDate: DateTime.now().minus({minute: 30}).set({second: 0, millisecond: 0}).toISO(),
  // endDate: DateTime.now().set({second: 0, millisecond: 0}).toISO(),
  // timestamp: DateTime.fromISO("2023-01-03T22:03:46.615447").toMillis(),
  startDate: 1672783425615,
  endDate: 1672783444615,
  // timestamp: DateTime.fromISO("2023-01-03T22:03:46.615447").toMillis(),
  lastTimestamp: 1672783425615,
  started: Date.now(),
  running: false,
  name: "Example Replay",
  controlsOpen: false,
  firstLoad: true,
  currentIMEIs: [],
  clientID: clientID, // Always set this
  isFromRemote: false,
  saving: false,
  loading: false,
  ready: true,
  error: null,
  message: null,
  forceStop: false,
  creationError: null,
};

export const replaySlice = createSlice({
  name: "replay",
  initialState: initialState,
  reducers: {
    set: (state, data: PayloadAction<ReplayConfig>) => {
      // Always preserve our client ID, never use the one from payload
      state = { ...data.payload, clientID };
      return state;
    },
    setTimestamp: (state, data: PayloadAction<number>) => {
      state.lastTimestamp = data.payload;
      return state;
    },
    update: (state, data: PayloadAction<Partial<ReplayConfig>>) => {
      // Generate a client ID if none exists
      if (!state.clientID) {
        state.clientID = Math.round(Math.random() * 10 ** 16);
      }

      // Don't let remote updates override our clientID
      if (
        data.payload.isFromRemote === true &&
        data.payload.clientID !== state.clientID
      ) {
        data.payload.clientID = state.clientID;
      }

      // If we're entering replay mode, always reset forceStop
      if (data.payload.isReplay === true) {
        data.payload.forceStop = false;
      }

      // Special handling for forceStop property
      if (data.payload.forceStop === true) {
        console.log("Processing forceStop in replay reducer");
        // When force stop is set, ensure we also set isReplay to false
        data.payload.isReplay = false;
        data.payload.running = false;
      }

      // Normal local update - calculate new state
      const newRunningState =
        data.payload.running !== undefined
          ? data.payload.running
          : state.running;

      state = {
        ...state,
        started: newRunningState ? Date.now() : state.started,
        ...data.payload,
        running: newRunningState, // never let undefined clobber running state
        clientID, // Always preserve our client ID
      };
      return state;
    },
    toggle: (state, data) => {
      state.isReplay = data.payload;
      return state;
    },
    toggleControls: (state, data) => {
      state.controlsOpen = data.payload;
      return state;
    },
    togglePlaying: (state, data?: PayloadAction<boolean | undefined>) => {
      const newRunningState = !state.running;
      state = {
        ...state,
        lastTimestamp: calculateTimestamp(state).toMillis(),
        running: data?.payload ?? newRunningState,
        firstLoad: false,
        clientID, // Always preserve our client ID
      };
      return state;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setSaving: (state, action: PayloadAction<boolean>) => {
      state.saving = action.payload;
    },

    setReady: (state, action: PayloadAction<boolean>) => {
      state.ready = action.payload;
    },

    setCreationError: (state, action: PayloadAction<string | null>) => {
      state.creationError = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  set,
  update,
  toggle,
  toggleControls,
  togglePlaying,
  setTimestamp,
  setSaving,
  setLoading,
  setReady,
  setCreationError,
} = replaySlice.actions;

export default replaySlice.reducer;

export interface ReplayConfig {
  isReplay: boolean;
  startDate: number;
  endDate: number;
  lastTimestamp: number;
  started: number;
  running: boolean;
  name: string;
  controlsOpen: boolean;
  firstLoad: boolean;
  currentIMEIs: number[];
  clientID?: number;
  isFromRemote: boolean;
  saving: boolean;
  loading: boolean;
  timezone?: string;
  ready?: boolean;
  error?: string | null;
  message?: string | null;
  forceStop: boolean;
  creationError?: string | null;
}

export function calculateTimestamp(config: ReplayConfig): DateTime {
  // console.log(config)
  if (!config.running) {
    return DateTime.fromMillis(config.lastTimestamp);
  }
  const sinceStart = Date.now() - config.started;
  const timestamp = config.lastTimestamp + sinceStart;
  if (timestamp >= config.endDate) {
    return DateTime.fromMillis(config.endDate);
  }
  return DateTime.fromMillis(timestamp);
}
